import { useState, useEffect } from "react";
import { calcMonthly } from "@/lib/providers/index";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import SandboxBadge from "@/components/SandboxBadge";

const SUPABASE_URL = "https://mfhdaiymdtwhsnaximji.supabase.co";
const PUB_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1maGRhaXltZHR3aHNuYXhpbWppIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYzMTE1MDUsImV4cCI6MjA3MTg4NzUwNX0.E-Mrze3hhKxO5hTf-j4iYa1v4AIdIs8zicdUjNatcRA";

type Offer = { id: string; total: string; currency: string; carrier: string; duration?: string };

async function duffelSearch(input: {
  origin: string; destination: string; departureDate: string; returnDate?: string; adults?: number;
}) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/duffel-search`, {
    method: "POST",
    headers: { "Authorization": `Bearer ${PUB_KEY}`, "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()) as { offers: Offer[] };
}

async function createLeadAndCheckout(params: {
  providerCode: "tabby"|"tamara"|"cashew";
  amountAED: number;
  customer: { name: string; email: string; phone?: string };
  metadata?: Record<string, any>;
  travelListingId: string;
  offer: Offer;
}) {
  let duffelOrderId = null;
  let holdFailed = false;

  // 1) Try to hold the Duffel offer first (if not sandbox)
  const duffelApiKey = "duffel_test_"; // This would come from your config
  if (!duffelApiKey.startsWith('duffel_test_')) {
    try {
      const holdRes = await fetch(`${SUPABASE_URL}/functions/v1/duffel-orders-hold`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${PUB_KEY}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          offer_id: params.offer.id,
          passengers: [{
            type: "adult",
            title: "mr",
            given_name: params.customer.name.split(' ')[0] || "John",
            family_name: params.customer.name.split(' ').slice(1).join(' ') || "Doe",
            email: params.customer.email,
            phone_number: params.customer.phone || "+971501234567"
          }]
        })
      });
      
      if (holdRes.ok) {
        const holdData = await holdRes.json();
        duffelOrderId = holdData.order_id;
        console.log('Duffel order held successfully:', duffelOrderId);
      } else {
        console.warn('Failed to hold Duffel order, will create immediately after BNPL approval');
        holdFailed = true;
      }
    } catch (error) {
      console.warn('Error holding Duffel order:', error);
      holdFailed = true;
    }
  }

  // 2) Create lead (edge function)
  const leadRes = await fetch(`${SUPABASE_URL}/functions/v1/lead-create`, {
    method: "POST",
    headers: { "Authorization": `Bearer ${PUB_KEY}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      listingId: params.travelListingId,
      providerCode: params.providerCode,
      name: params.customer.name,
      email: params.customer.email,
      phone: params.customer.phone,
      utm: params.metadata?.utm ?? {}
    })
  });
  if (!leadRes.ok) throw new Error(await leadRes.text());
  const { id: leadId } = await leadRes.json();

  // 3) Create provider checkout link (server function)
  const checkoutMetadata: Record<string, any> = { 
    leadId, 
    listingId: params.travelListingId, 
    providerCode: params.providerCode,
    offer_id: params.offer.id,
    ...params.metadata
  };

  // Include Duffel order ID if hold was successful
  if (duffelOrderId) {
    checkoutMetadata.duffel_order_id = duffelOrderId;
  }

  const checkoutRes = await fetch(`${SUPABASE_URL}/functions/v1/provider-checkout`, {
    method: "POST",
    headers: { "Authorization": `Bearer ${PUB_KEY}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      providerCode: params.providerCode,
      amountAED: params.amountAED,
      customer: params.customer,
      metadata: checkoutMetadata,
      returnUrl: `${window.location.origin}/booking/confirmed?leadId=${leadId}`
    })
  });
  if (!checkoutRes.ok) throw new Error(await checkoutRes.text());
  const { url } = await checkoutRes.json();
  
  // For sandbox/demo, redirect to confirmation immediately
  if (duffelApiKey.startsWith('duffel_test_')) {
    const demoConfirmUrl = `/booking/confirmed?amount=${encodeURIComponent(params.amountAED)}&currency=AED&provider=${encodeURIComponent(params.providerCode)}&ref=${encodeURIComponent('BNPL-' + Date.now())}&leadId=${leadId}`;
    window.location.href = demoConfirmUrl;
  } else {
    // Redirect to real BNPL provider checkout
    window.location.href = url;
  }
}

export default function Flights() {
  const [origin, setOrigin] = useState("DXB");
  const [destination, setDestination] = useState("ATH");
  const [departureDate, setDepartureDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [adults, setAdults] = useState(1);
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [providerCode, setProviderCode] = useState<"tabby"|"tamara"|"cashew">("tabby");
  const [travelListingId, setTravelListingId] = useState<string>("");

  // Get the travel listing ID on component mount
  useEffect(() => {
    const fetchTravelListing = async () => {
      const { data, error } = await supabase
        .from('listings')
        .select('id')
        .eq('title', 'Flight Booking (Demo)')
        .eq('category', 'travel')
        .single();
      
      if (data && !error) {
        setTravelListingId(data.id);
      } else {
        console.error('Failed to fetch travel listing:', error);
        toast.error('Failed to load travel configuration');
      }
    };

    fetchTravelListing();
  }, []);

  const onSearch = async () => {
    if (!origin || !destination || !departureDate) {
      toast.error("Please fill origin, destination, and departure date");
      return;
    }
    setLoading(true);
    try {
      const { offers } = await duffelSearch({ origin, destination, departureDate, returnDate: returnDate || undefined, adults });
      setOffers(offers);
      if (offers.length === 0) toast.info("No offers found. Try different dates.");
    } catch (e: any) {
      toast.error(e.message ?? "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const onCheckout = async (offer: Offer) => {
    if (!travelListingId) {
      toast.error("Travel configuration not loaded");
      return;
    }

    const total = Number(offer.total ?? 0);
    const name = (document.getElementById("bnplName") as HTMLInputElement)?.value || "Demo User";
    const email = (document.getElementById("bnplEmail") as HTMLInputElement)?.value || "demo@example.com";
    const phone = (document.getElementById("bnplPhone") as HTMLInputElement)?.value || "";

    try {
      await createLeadAndCheckout({
        providerCode,
        amountAED: total,
        customer: { name, email, phone },
        metadata: { utm: { source: "travel_flights_demo" } },
        travelListingId,
        offer
      });
    } catch (e: any) {
      toast.error(e.message ?? "Checkout failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-3 text-foreground">Search flights</h2>
      <SandboxBadge />
      <div className="grid md:grid-cols-6 gap-3 items-end mb-4">
        <div>
          <label className="text-sm text-muted-foreground">Origin (IATA)</label>
          <input 
            className="w-full border border-border rounded-lg px-3 py-2 bg-background text-foreground" 
            value={origin} 
            onChange={e=>setOrigin(e.target.value.toUpperCase())}
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground">Destination (IATA)</label>
          <input 
            className="w-full border border-border rounded-lg px-3 py-2 bg-background text-foreground" 
            value={destination} 
            onChange={e=>setDestination(e.target.value.toUpperCase())}
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground">Depart</label>
          <input 
            type="date" 
            className="w-full border border-border rounded-lg px-3 py-2 bg-background text-foreground" 
            value={departureDate} 
            onChange={e=>setDepartureDate(e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground">Return (optional)</label>
          <input 
            type="date" 
            className="w-full border border-border rounded-lg px-3 py-2 bg-background text-foreground" 
            value={returnDate} 
            onChange={e=>setReturnDate(e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground">Adults</label>
          <input 
            type="number" 
            min={1} 
            className="w-full border border-border rounded-lg px-3 py-2 bg-background text-foreground" 
            value={adults} 
            onChange={e=>setAdults(Math.max(1, Number(e.target.value)))}
          />
        </div>
        <div>
          <button 
            onClick={onSearch} 
            disabled={loading} 
            className="w-full rounded-lg px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? "Searching..." : "Search"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <select 
          className="border border-border rounded-lg px-3 py-2 bg-background text-foreground" 
          value={providerCode} 
          onChange={e=>setProviderCode(e.target.value as any)}
        >
          <option value="tabby">Tabby</option>
          <option value="tamara">Tamara</option>
          <option value="cashew">Cashew</option>
        </select>
        <input 
          id="bnplName" 
          placeholder="Your name" 
          className="border border-border rounded-lg px-3 py-2 bg-background text-foreground"
        />
        <input 
          id="bnplEmail" 
          placeholder="Email" 
          className="border border-border rounded-lg px-3 py-2 bg-background text-foreground"
        />
        <input 
          id="bnplPhone" 
          placeholder="Phone (optional)" 
          className="border border-border rounded-lg px-3 py-2 bg-background text-foreground"
        />
      </div>

      {offers.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          {offers.map((o) => {
            const total = Number(o.total ?? 0);
            const example = calcMonthly(total, 12);
            return (
              <div key={o.id} className="border border-border rounded-2xl p-4 bg-card">
                <div className="flex justify-between">
                  <div>
                    <div className="text-sm text-muted-foreground">{o.carrier || "Carrier"}</div>
                    <div className="text-xl font-semibold text-foreground">AED {total.toLocaleString()}</div>
                  </div>
                  <div className="text-sm text-muted-foreground">{o.duration ? o.duration.replace("PT","") : ""}</div>
                </div>

                <div className="mt-3 text-sm">
                  <div className="text-foreground">Example: <strong>AED {example}/mo</strong> over 12 months via <strong>{providerCode}</strong></div>
                  <div className="text-muted-foreground">Subject to provider approval. Flexaura is a marketplace and does not lend.</div>
                </div>

                <div className="mt-3 flex gap-2">
                  <button 
                    onClick={() => onCheckout(o)} 
                    className="rounded-lg bg-primary text-primary-foreground px-4 py-2 hover:bg-primary/90"
                  >
                    Pay in installments
                  </button>
                  <button className="rounded-lg border border-border px-4 py-2 text-foreground hover:bg-accent">
                    Pay in full
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}